import React, { forwardRef, useCallback, useState, useRef, useEffect, useMemo, useImperativeHandle } from 'react'
import useResizeObserver from '@react-hook/resize-observer'

import DataGrid, {
    DataGridRef,
    ColumnChooser,
    FilterRow,
    GroupPanel,
    Grouping,
    HeaderFilter,
    LoadPanel,
    Pager,
    Paging,
    SearchPanel,
    Selection,
    Scrolling,
    RowDragging,
    StateStoring,
} from 'devextreme-react/data-grid'
import { Template } from 'devextreme-react/core/template'
import { EditorPreparingEvent } from 'devextreme/ui/data_grid'
import { ISelectionProps } from 'devextreme-react/data-grid'
import { Popup } from 'devextreme-react/popup'
import FileUploader from 'devextreme-react/file-uploader'
import { IxBadge } from 'components/badge'

import { LayoutChooser } from 'components/layout-chooser'
import { useIxGlobalFilter } from 'contexts/global-filter-context'
import { excelExporter } from 'helpers/excel-exporter'
import { pdfExporter } from 'helpers/pdf-exporter'
import { filterBuilder } from 'helpers/filter-builder'
import { useTranslation } from 'react-i18next'
import { BaseIcons } from 'enums/base-icons'
import _ from 'lodash'
import { Selection as SelectionEnum } from 'enums/selection'

import './base-grid.scss'

const allowedFileExtensions = ['.xls', '.xlsx', '.xlsb', '.xltx', '.xltm', '.xlsm', '.xml'];

let singleClick = false

export type IBaseGridProps = {
    name?: string,
    loading?: boolean,
    items: any[],
    onSelectionChanged?: (ids: any[]) => void,
    children?: React.ReactNode | React.ReactNode[],
    objectFilterField: string,
    fromFilterField: string,
    selectionMode?: ISelectionProps['mode'],
    keyExpr?: string,
    selectedRowKeys?: any[],
    onDblClick?: (e: any[]) => void,
    customToolbarItems?: any[]
    customizeColumns?: any,
    enabledLoadPanel?: boolean | 'auto',
    height?: any,
    onEditorPreparing?: (e: EditorPreparingEvent) => void
    enablePaging?: boolean,
    draggingProps?: any,
    defaultSelectionFilter?: any[],
    deferredSelection?: boolean,
    columnRenderingMode?: 'standard' | 'virtual',
    rowContextMenu?: Function,
    showCheckBoxesMode?: 'none' | 'onClick' | 'always' | 'onLongTap' | undefined,
    selection?: SelectionEnum,
    onOptionChanged?: (e: any) => void,
    onContentReady?: (e: any) => void,
    filterVisible?: boolean,
    moduleName?: string,
}

export const BaseGrid = forwardRef<(DataGridRef), IBaseGridProps>((
    {
        name, items, onSelectionChanged, onDblClick, objectFilterField,
        fromFilterField, selectionMode='multiple', customToolbarItems,
        enabledLoadPanel='auto', onEditorPreparing, deferredSelection=false,
        columnRenderingMode='virtual', draggingProps, enablePaging=true,
        rowContextMenu, showCheckBoxesMode='onClick', selection,
        filterVisible=false, moduleName, ...props
    },
    ref
) => {
    const [template, setTemplate] = useState<File | null>(null)
    const [autoExpandAll, setAutoExpandAll] = useState(true)
    const datagrid = useRef<DataGridRef>(null)
    const { objectFilter, fromFilter } = useIxGlobalFilter()
    const { i18n, t } = useTranslation()
    const [popupVisible, setPopupVisible] = useState(false)
    const [isDropZoneActive, setIsDropZoneActive] = useState(false)
    const [layoutsChooserVisible, setLayoutsChooserVisible] = useState(false)
    const [gridHeight, setGridHeight] = useState<any>(250)

    useImperativeHandle(ref, () => datagrid.current! )

    const togglePopup = useCallback(() => {
        setPopupVisible(!popupVisible)
      },[popupVisible])

    const toggleLayoutChooser = useCallback(() => {
        setLayoutsChooserVisible(visible => !visible)
    }, [])

    const onExporting = useCallback((e: any) => {
        if(!datagrid.current) {return}
        const dataGrid = datagrid.current.instance()
        const fileName = name || 'Datagrid'
        e.cancel = true

        e.itemData.fileType === 'Excel' && excelExporter(dataGrid, fileName, template, e.itemData.isSelectedRow)
        e.itemData.fileType === 'PDF' && pdfExporter(dataGrid, e.itemData.isSelectedRow)

    }, [name, template])

    const exportingBtns = useMemo(() => (
    [
        { id: 1, name: `${t('global:button.exportAll')} (Excel)`, fileType: 'Excel', isSelectedRow: false, icon: BaseIcons.GenerateAllAsExcel, onClick: onExporting},
        { id: 2, name: `${t('global:button.exportSelected')} (Excel)`, fileType: 'Excel', isSelectedRow: true,  icon: BaseIcons.GenerateAsExcel, onClick: onExporting},
        { id: 3, name: `${t('global:button.exportAll')} (PDF)`, fileType: 'PDF', isSelectedRow: false, icon: BaseIcons.GenerateAllAsPdf, onClick: onExporting},
        { id: 4, name: `${t('global:button.exportSelected')} (PDF)`, fileType: 'PDF', isSelectedRow: true, icon: BaseIcons.GenerateAsPdf , onClick: onExporting},
        { id: 5, name: `${t('global:button.changeTemplate')}`, icon: 'exportselected', onClick: togglePopup},
    ]), [t, togglePopup, onExporting])

    const onValueChanged = (e: any) => {
        setTemplate(e.value[0])
        setIsDropZoneActive(false)
    }

    const onDropZoneEnter = (e: any) => {
        if(e.dropZoneElement.id === 'dropzone-external') {
          setIsDropZoneActive(true)
        }
    }

    const onDropZoneLeave = (e: any) => {
        if(e.dropZoneElement.id === 'dropzone-external') {
          setIsDropZoneActive(false)
        }
    }

    const onToolbarPreparing = useCallback((e: any) => {
        let toolbarItems = e.toolbarOptions.items;
        toolbarItems.unshift({
            location: 'after',
            widget: 'dxDropDownButton',
            options: {
                icon: BaseIcons.Export,
                stylingMode: 'text',
                dropDownOptions: {
                    width: 205,
                    height: 'auto',
                    onContentReady: (e: any) => {e.component.content().classList.add('popup-exporting')}
                },
                items: exportingBtns,
                displayExpr: 'name',
                keyExpr: 'id',
                showArrowIcon: false,
                splitButton: false,
                elementAttr: {class: 'drop-down-button'},
              }})
         toolbarItems.splice(3, 0, {
            widget: 'dxButton',
            options: {
                icon: 'detailslayout',
                onClick: toggleLayoutChooser
            },
            location: 'after'
        })

        if (customToolbarItems) {
            toolbarItems.unshift(...customToolbarItems)
        }
    }, [exportingBtns, toggleLayoutChooser, customToolbarItems])

    const _onSelectionChanged = useCallback((e: any) => {
        if (props.selectedRowKeys !== undefined && !deferredSelection) {
            let selectedRows = props.selectedRowKeys.filter((r: any) => e.currentDeselectedRowKeys.indexOf(r) === -1)
            if (!_.isEqual(selectedRows, e.currentSelectedRowKeys)) {
                selectedRows = selectedRows.concat(e.currentSelectedRowKeys)
            }
            onSelectionChanged && onSelectionChanged(selectedRows)
        } else {
            onSelectionChanged && onSelectionChanged([])
        }
    }, [onSelectionChanged, props.selectedRowKeys, deferredSelection])

    const onContextMenuPreparing = useCallback((e: any) => {
        if ((e.target === 'content' && e.row?.rowType === 'group') ||
            (e.target === 'header' && e.column !== undefined)) {

            /* v8 ignore next */
            if (!e.items) { e.items = [] }

            e.items.push({ text: 'Collapse All', onItemClick: () => setAutoExpandAll(false) })
            e.items.push({ text: 'Expand All', onItemClick: () => setAutoExpandAll(true) })
        } else if (e.target === 'content' && e.row?.rowType === 'data') {
            rowContextMenu && rowContextMenu(e)
        }
    }, [rowContextMenu])

    const _onRowClick = useCallback((e: any) => {
        if (e.event.detail === 1 && deferredSelection) {
            singleClick = true
            setTimeout(() => {
                if (!singleClick) { return }
                if (!e.isSelected) {
                    e.component.selectRows([e.key], selectionMode === 'multiple' && (selection && selection > SelectionEnum.SingleSelection))
                } else if (selectionMode !== 'single') {
                    e.component.deselectRows([e.key])
                }
            }, 500)
        }
    }, [selectionMode, selection, deferredSelection])

    const _onDblClick = useCallback((e: any) => {
        singleClick = false
        const newKey = e.key
        const handleDblClick = (selectedRows: any[]) => {
            if (selectedRows.length > 1) { return }
            if (!e.isSelected) {
                datagrid.current?.instance()?.selectRows([newKey], false).then((_: any[]) => {
                    setTimeout(() => { onDblClick && onDblClick([newKey]) })
                })
            } else {
                onDblClick && onDblClick(selectedRows)
            }
        }

        if (deferredSelection) {
            datagrid.current?.instance()?.getSelectedRowKeys().then(handleDblClick)
        } else {
            const _selectedRows = e.component.getSelectedRowKeys()
            handleDblClick(_selectedRows)
        }
    }, [onDblClick, deferredSelection])

    useEffect(() => {
        if (props.loading) {
            /* v8 ignore next */
            datagrid.current?.instance() && datagrid.current.instance().beginCustomLoading('loading')
        } else {
            /* v8 ignore next */
            datagrid.current?.instance() && datagrid.current.instance().endCustomLoading()
        }
    }, [props.loading])

    useEffect(() => {
        if (datagrid.current) {
            if ((objectFilter && objectFilterField) || (fromFilter && fromFilterField)) {
                const filter = filterBuilder(objectFilter, objectFilterField, fromFilter, fromFilterField)

                /* v8 ignore next 5 */
                if (filter.length) {  // don't apply filter if it's an empty array
                    datagrid.current?.instance() && datagrid.current.instance().filter(filter)
                } else {
                    datagrid.current?.instance() && datagrid.current.instance().clearFilter('dataSource')
                }
            } else {
                /* v8 ignore next */
                datagrid.current?.instance() && datagrid.current.instance().clearFilter('dataSource')
            }
        }
    }, [objectFilter, objectFilterField, fromFilter, fromFilterField])

    useEffect(() => {
        /* v8 ignore next 3 */
        const repaint = () => {
            datagrid.current?.instance()?.repaint()
        }

        i18n.on('languageChanged', repaint)

        return () => { i18n.off('languageChanged', repaint) }
    }, [i18n])

    const _onEditorPreparing = useCallback((e: EditorPreparingEvent) => {
        if (e.editorName !== 'dxSelectBox') {
            e.editorOptions.valueChangeEvent = 'change'
        }
        onEditorPreparing && onEditorPreparing(e)
    }, [onEditorPreparing])

    const selectedContextBadge = useCallback((data: any) => {
        return (
            <div style={ { display: 'flex', alignItems: 'center' } }><IxBadge value={ data.nSelected } /> SELECTED</div>
        )
    }, [])

    const saveState = useCallback((state: any) => {
        const { selectionFilter, selectedRowKeys, ...partialState } = state
        localStorage.setItem(`${name}-grid`, JSON.stringify(partialState))
    }, [name])

    const loadState = useCallback(() => {
        let state: any = localStorage.getItem(`${name}-grid`)

        if (state) {
            state = JSON.parse(state)
            if (state.pageSize === 0) { state.pageSize = 50 }
        }

        return state ? state : undefined
    }, [name])

    const onCellPrepared = useCallback((e: any) => {
        if (e.column.dataField === 'AUFGABESTATUS' || e.column.dataField === 'AKTIONSTATUS') {
            e.value === 2 && (e.cellElement.style.color = 'red')
            e.value === 1 && (e.cellElement.style.color = 'green')
            e.value === 0 && (e.cellElement.style.color = 'blue')
        }
    },[])

    useEffect(() => {
        setTimeout(() => datagrid.current?.instance()?.option('selectionFilter', props.defaultSelectionFilter ? [ ...props.defaultSelectionFilter ] : []), 0)
    }, [props.defaultSelectionFilter])

    const datagridContainer = useRef<HTMLDivElement>(null)
    useResizeObserver(document.querySelector('#content'), () => {
        const bodyHeight = document.body.getBoundingClientRect().height
        const gridTop = datagridContainer.current?.getBoundingClientRect().top
        setGridHeight(bodyHeight - (gridTop || 45) - 45)
    })

    React.useLayoutEffect(() => {
        if (filterVisible === undefined) { return }

        const bodyHeight = document.body.getBoundingClientRect().height
        const gridTop = datagridContainer.current?.getBoundingClientRect().top
        gridTop && setGridHeight(bodyHeight - gridTop - 45)
    }, [filterVisible])

    return (
        <div ref={ datagridContainer } style={ { margin: '0.5rem' } }>
            <Popup
                visible={popupVisible}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                onHiding={togglePopup}
                deferRendering={false}
                title={t('global:label.selectTemplate')}
                width={400}
                height={285}
            >
                <div className="widget-container flex-box">
                    <div id="dropzone-external" className={`flex-box ${isDropZoneActive ? 'dx-theme-accent-as-border-color dropzone-active' : 'dx-theme-border-color'}`}>
                        {
                            !template ?
                                <div id="dropzone-text" className="flex-box">
                                    <span>{t('global:label.textDrag')}</span>
                                    <span>{t('global:label.textClick')}</span>
                                </div>
                                : allowedFileExtensions.find((extension) =>
                                    template.name.endsWith(extension))
                                    ? <span>{template.name}</span>
                                    : <span>{t('global:error.incorrectFile')}</span>
                        }
                    </div>
                    <FileUploader
                        id="file-uploader"
                        dialogTrigger="#dropzone-external"
                        dropZone="#dropzone-external"
                        multiple={false}
                        allowedFileExtensions={allowedFileExtensions}
                        uploadMode="useButtons"
                        visible={false}
                        onValueChanged={onValueChanged}
                        onDropZoneEnter={onDropZoneEnter}
                        onDropZoneLeave={onDropZoneLeave}
                    />
                </div>
            </Popup>

            <LayoutChooser
                moduleName={moduleName}
                gridName={name || ''}
                datagrid={datagrid}

                visible={layoutsChooserVisible}
                onHiding={ () => setLayoutsChooserVisible(false) }
            />

            <DataGrid
                id={`${name}-grid`}
                ref={ datagrid }
                dataSource={items}
                keyExpr={props.keyExpr || 'HDR_ID'}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                showColumnLines={true}
                onToolbarPreparing={onToolbarPreparing}
                onSelectionChanged={_onSelectionChanged}
                onContextMenuPreparing={onContextMenuPreparing}
                columnAutoWidth={true}
                columnMinWidth={100}
                columnResizingMode='widget'
                onRowClick={_onRowClick}
                onRowDblClick={_onDblClick}
                onCellPrepared={onCellPrepared}
                // style={{maxHeight: `${gridHeight}px`}}
                height={props.height || gridHeight}
                // repaintChangesOnly={ true }
                // if `props.selectedRowKeys` is not provided we don't set the
                // datagrid `selectedRowKeys` prop so selection still works
                { ...(props.selectedRowKeys ? { selectedRowKeys: props.selectedRowKeys } : {}) }
                { ...(props.customizeColumns ? {customizeColumns: props.customizeColumns} : {}) }
                { ...(props.onOptionChanged ? {onOptionChanged: props.onOptionChanged} : {}) }
                onContentReady={props.onContentReady}
                onEditorPreparing={ _onEditorPreparing }
                // { ...(props.defaultSelectionFilter ? { defaultSelectionFilter: props.defaultSelectionFilter } : {}) }
            >
                { draggingProps && <RowDragging {...draggingProps} /> }
                <GroupPanel visible={true} />
                <SearchPanel visible={true} />
                <Grouping contextMenuEnabled={true} autoExpandAll={autoExpandAll} />
                <Paging defaultPageSize={50} enabled={enablePaging}/>
                <Pager showPageSizeSelector={true} allowedPageSizes={[10, 25, 50, 100]}/>
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                <LoadPanel
                    enabled={enabledLoadPanel}
                    showIndicator={true}
                />
                <ColumnChooser enabled={true} />
                <Selection
                    mode={selectionMode}
                    showCheckBoxesMode={deferredSelection ? 'always' : showCheckBoxesMode}
                    deferred={deferredSelection}
                />
                <Scrolling
                    scrollByContent={true}
                    columnRenderingMode={ columnRenderingMode }
                    // rowRenderingMode='virtual'
                />
                <Template name='selectedContextBadge' render={ selectedContextBadge } />
                <StateStoring
                    enabled={ true }
                    savingTimeout={ 1000 }
                    type='custom'
                    customLoad={ loadState }
                    customSave={ saveState }
                />

                { props.children }
            </DataGrid>
        </div>
    )
})
